import { warn } from '@execonline-inc/logging';
import { InvalidUrlError } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import InvitationStore from '.';
import { AppyError, callApi } from '../../../../../../Appy';
import { handleError } from '../../../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../../../LinkyLinky';
import ReactionComponent from '../../../../../../ReactionComponent';
import { Link } from '../../../../../../Resource/Types';
import { invitationResourceDecoder } from '../Decoders';
import { invitationEncoder } from './Encoders';
import { Deactivating, Reactivating, State } from './Types';

interface Props {
  store: InvitationStore;
}

export type InvitationError = AppyError | InvalidUrlError | MissingLinkError;

export const handleInvitationError = (store: InvitationStore) => (error: InvitationError) => {
  switch (error.kind) {
    case 'missing-link-error':
    case 'invalid-url-error':
      store.error('We are unable to process your request at this time');
      warn('ParticipantsReactions', error.kind, error);
      break;
    default:
      handleError(store, error);
  }
};

const deactivate = (store: InvitationStore, state: Deactivating) =>
  Task.succeed<InvitationError, ReadonlyArray<Link>>(state.resource.links)
    .andThen(findLinkT('deactivate'))
    .andThen(callApi(invitationResourceDecoder, invitationEncoder(store, state)))
    .fork(handleInvitationError(store), (resource) => {
      store.deactivateSucceeded(resource);
      store.invitationsStore.updateInvitationInInvitations(resource);
    });

const reactivate = (store: InvitationStore, state: Reactivating) =>
  Task.succeed<InvitationError, ReadonlyArray<Link>>(state.resource.links)
    .andThen(findLinkT('reactivate'))
    .andThen(callApi(invitationResourceDecoder, invitationEncoder(store, state)))
    .fork(handleInvitationError(store), (resource) => {
      store.reactivateSucceeded(resource);
      store.invitationsStore.updateInvitationInInvitations(resource);
    });

class InvitationReactions extends ReactionComponent<InvitationStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'deactivating':
        deactivate(store, state);
        break;
      case 'reactivating':
        reactivate(store, state);
        break;
      case 'waiting':
      case 'confirming-deactivate':
      case 'error':
      case 'deactivate-succeeded':
      case 'reactivate-succeeded':
        break;
      default:
        assertNever(state);
    }
  };
}

export default InvitationReactions;
